/**
 * CSOnboardingListPanel — Section 9 (iter-137). Customer Service view
 * of the New Account Onboarding checklist. Reuses the same data +
 * endpoints as the (now-retired) Operations panel but renders each
 * account as a compact one-line row.
 *
 * Row: property name · property type · account manager · start date ·
 *      2/10 progress · next incomplete step.
 * Click a row → the same row inline-expands to the full 10-step
 * checklist with completion stamps (who / when) — no other UI moves.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, ClipboardCheck, RefreshCw, PartyPopper } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';

function progressOf(d) {
  const items = d.onboarding_checklist || [];
  const done = items.filter((i) => i.done).length;
  return { done, total: items.length, pct: items.length ? Math.round((done / items.length) * 100) : 0 };
}

function nextIncompleteOf(d) {
  const items = d.onboarding_checklist || [];
  const idx = items.findIndex((i) => !i.done);
  return idx === -1 ? null : { index: idx + 1, label: items[idx].label };
}

export default function CSOnboardingListPanel({ testId = 'cs-onboarding-list' }) {
  const [drafts, setDrafts] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/api/service-packages/draft-contracts');
      setDrafts(data.drafts || []);
    } catch (_) { setDrafts([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleStep = async (d, step, checked) => {
    try {
      const res = await apiFetch(
        `/api/service-packages/draft-contracts/${d.draft_contract_id}/checklist/${step.key}`,
        { method: 'PATCH', body: { done: checked } });
      if (res.ready_for_day_one) toast.success(`${d.property_name || d.company_name} is ready for day one!`);
      load();
    } catch (e) { toast.error(e.detail || 'Update failed'); }
  };

  if (drafts === null) {
    return (
      <p className="text-xs text-zinc-500" data-testid={`${testId}-loading`}>Loading onboarding accounts…</p>
    );
  }
  const pending = drafts.filter((d) => progressOf(d).done < progressOf(d).total).length;

  return (
    <section className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <ClipboardCheck className="h-4 w-4" /> New Account Onboarding · {pending} in progress
        </h2>
        <button onClick={load} className="p-1.5 rounded-full border border-zinc-600 text-zinc-300 hover:text-white" data-testid={`${testId}-refresh`}>
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {drafts.length === 0 ? (
        <p className="text-xs text-zinc-500 py-6 text-center" data-testid={`${testId}-empty`}>
          No accounts currently in onboarding.
        </p>
      ) : (
        <div className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 overflow-hidden">
          {drafts.map((d) => {
            const { done, total } = progressOf(d);
            const nx = nextIncompleteOf(d);
            const ready = total > 0 && done === total;
            const isOpen = openId === d.draft_contract_id;
            return (
              <div key={d.draft_contract_id} data-testid={`${testId}-row-${d.draft_contract_id}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : d.draft_contract_id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-800/40 transition-colors"
                  data-testid={`${testId}-toggle-${d.draft_contract_id}`}
                >
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                          : <ChevronRight className="h-3.5 w-3.5 text-zinc-500 shrink-0" />}
                  <span className="flex-1 min-w-0 grid grid-cols-12 items-center gap-2 text-[11px]">
                    <span className="col-span-3 truncate text-white font-semibold">
                      {d.property_name || d.company_name}
                    </span>
                    <span className="col-span-2 truncate uppercase tracking-widest text-[9px]" style={{ color: GOLD }}>
                      {d.business_type || '—'}
                    </span>
                    <span className="col-span-2 truncate text-zinc-300">
                      AM: {d.account_manager || '—'}
                    </span>
                    <span className="col-span-2 truncate text-zinc-400">
                      {d.service_start_date || 'TBD'}
                    </span>
                    <span className={`col-span-1 font-mono font-bold ${ready ? 'text-emerald-400' : 'text-zinc-200'}`} data-testid={`${testId}-progress-${d.draft_contract_id}`}>
                      {done}/{total}
                    </span>
                    <span className="col-span-2 truncate text-zinc-500 italic">
                      {ready
                        ? <span className="text-emerald-400 not-italic inline-flex items-center gap-1"><PartyPopper className="h-3 w-3" /> Ready Day One</span>
                        : nx ? `Next: ${nx.index}. ${nx.label}` : '—'}
                    </span>
                  </span>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 pl-6 space-y-1 bg-zinc-950/40" data-testid={`${testId}-detail-${d.draft_contract_id}`}>
                    {(d.onboarding_checklist || []).map((step, idx) => (
                      <label key={step.key}
                        className={`flex items-start gap-2.5 p-2 rounded cursor-pointer transition-colors ${step.done ? 'bg-emerald-950/30' : 'hover:bg-zinc-800/60'}`}
                        data-testid={`${testId}-step-${d.draft_contract_id}-${step.key}`}>
                        <input type="checkbox" checked={!!step.done}
                          onChange={(e) => toggleStep(d, step, e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-zinc-600 accent-[#C9A962]"
                          data-testid={`${testId}-check-${d.draft_contract_id}-${step.key}`} />
                        <span className="flex-1">
                          <span className={`text-xs ${step.done ? 'text-zinc-500 line-through' : 'text-zinc-200'}`}>
                            {idx + 1}. {step.label}
                          </span>
                          {step.done && step.done_by && (
                            <span className="block text-[10px] text-emerald-500 mt-0.5">
                              {step.done_by} · {step.done_at ? new Date(step.done_at).toLocaleString() : 'time n/a'}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
